import React, { useEffect, useRef, useState } from 'react';
import { initWasabiContexts } from '@tracking';
import translations from '../translations';
import { TransferPageComponent } from './Pages/TransferPage';
import { logInfo } from './shared/logger';
import ErrorBoundary from './shared/components/ErrorBoundary';
import IntlProvider from './shared/components/WithIntl/IntlProvider';
import getRedirect from './redirection/getRedirect';
import getWindow from './shared/dom/getWindow';
import getUserLocale from './shared/dom/getUserLocale';

import './App.css';

const getCurrentRedirect = () => {
  const window = getWindow();
  const location = window?.location;
  const hash = location?.hash || '';
  const hostname = location?.hostname || '';
  const origin = location?.origin || '';
  initWasabiContexts(hash);

  const userLocale = getUserLocale(window);

  const redirect = getRedirect({ hash, hostname, origin, userLocale });

  logInfo(
    `Generated redirect state from site location of ${redirect.payload.siteLocation}:`,
    redirect,
  );

  return redirect;
};

const jsonEquals = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a) === JSON.stringify(b);

// No automatic redirect refresh should happen within 30 seconds, unless the parameters (hash changed):
const REFRESH_INTERVAL_MILLISECONDS = 30_000;

const App = (): React.JSX.Element => {
  const [redirect, setRedirect] = useState(getCurrentRedirect);

  // As multiple events may be dispatched in the same loop cycle,
  // the ref is used to prevent changes being applied
  // while the state object is not immediately synchronized.
  const redirectRef = useRef(redirect);

  useEffect(() => {
    logInfo(
      `Transfer page shown from site location of ${redirect.payload.siteLocation}:`,
      redirect,
    );

    const refresh = () => {
      const currentRedirect = redirectRef.current;
      const refreshedRedirect = getCurrentRedirect();

      const { now: currentRedirectCreatedAt, queryParams: stableCurrentRedirect } = currentRedirect;
      const { now: refreshedRedirectCreatedAt, queryParams: stableRefreshedRedirect } =
        refreshedRedirect;

      // Trigger redirect refresh if it differs, or it is stale. The refresh prevention is to just avoid
      // generating deep links twice during common flows when users switch tab early enough.
      // To compute equality, the redirect is serialized by stable parameters (queryParams = hash),
      // excluding the created now field, which varies for every redirect.
      const differs = !jsonEquals(stableRefreshedRedirect, stableCurrentRedirect);
      const notEarlierThan = new Date(
        currentRedirectCreatedAt.valueOf() + REFRESH_INTERVAL_MILLISECONDS,
      );
      const fresh = refreshedRedirectCreatedAt > notEarlierThan;

      if (differs || fresh) {
        // This ref is used to sync state immediately:
        redirectRef.current = refreshedRedirect;
        setRedirect(refreshedRedirect);
      }
    };

    // Various visibility/history manipulation events are subscribed
    // to provide full coverage across non-conformant browsers, for example
    // Safari with the back-forward cache on:
    const onFocus = () => {
      refresh();
    };

    const onHashChange = () => {
      refresh();
    };

    const onPageShow = () => {
      refresh();
    };

    const onPopState = () => {
      refresh();
    };

    const onVisibilityChange = () => {
      const { hidden } = document;

      if (!hidden) {
        refresh();
      }
    };

    addEventListener('focus', onFocus, { passive: true });
    addEventListener('hashchange', onHashChange, { passive: true });
    addEventListener('pageshow', onPageShow, { passive: true });
    addEventListener('popstate', onPopState, { passive: true });
    addEventListener('visibilitychange', onVisibilityChange, { passive: true });

    return () => {
      removeEventListener('focus', onFocus);
      removeEventListener('hashchange', onHashChange);
      removeEventListener('pageshow', onPageShow);
      removeEventListener('popstate', onPopState);
      removeEventListener('visibilitychange', onVisibilityChange);
    };
    // This must only run once, the `redirect` is only used for logging purposes on the initial run:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { locale } = redirect;
  const messages = translations[locale];

  return (
    <ErrorBoundary>
      <IntlProvider locale={locale} messages={messages}>
        <TransferPageComponent redirect={redirect} />
      </IntlProvider>
    </ErrorBoundary>
  );
};

export default App;
